import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  ieltsReadingApi,
  TF_NG_OPTIONS,
  YN_NG_OPTIONS,
  type ReadingQuestion,
  type ReadingQuestionType,
  type ReadingTest,
  type ReadingTestInput,
} from '@/api/ielts';
import { humanizeError } from '@/lib/errors';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card, CardTitle } from '@/components/ui/Card';
import { Pill } from '@/components/ui/Pill';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { useConfirm } from '@/components/ui/ConfirmProvider';
import { ActionSheet } from '@/components/mobile/ActionSheet';
import { MAdd, MBlock, MEmpty, MRow, MTitle } from '@/components/mobile/MobileKit';
import { MoreButton } from '@/components/mobile/MoreButton';
import { useIsMobile } from '@/hooks/useIsMobile';

/**
 * IELTS Reading tests — admin/manager content management (Feature #3c).
 *
 * Mirrors AdminIeltsWritingPage: a list of cards + a create/edit modal. The
 * editor manages the passage HTML + an ordered LIST of questions (add / remove /
 * reorder) with a small per-type sub-editor — the #5 multi-question pattern.
 * Reachable by both `admin` and `manager` (content is not financial); the server
 * enforces RequireRole("admin","manager").
 */

const QUESTION_TYPES: ReadingQuestionType[] = [
  'multiple_choice',
  'true_false_ng',
  'yes_no_ng',
  'sentence_completion',
  'short_answer',
  'matching_headings',
];

/** Types whose answer is an option index (vs. an enum / free string). */
const INDEX_TYPES: ReadingQuestionType[] = ['multiple_choice', 'matching_headings'];

let qidSeq = 0;
function newQuestion(): ReadingQuestion {
  qidSeq += 1;
  return {
    id: `q-${Date.now()}-${qidSeq}`,
    type: 'multiple_choice',
    prompt: '',
    options: ['', ''],
    answer: 0,
  };
}

export function AdminIeltsReadingPage() {
  const ask = useConfirm();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [creatingOpen, setCreatingOpen] = useState(false);
  const [editing, setEditing] = useState<ReadingTest | null>(null);
  const [sheetFor, setSheetFor] = useState<ReadingTest | null>(null);
  const isMobile = useIsMobile();

  const list = useQuery({
    queryKey: ['admin', 'ielts', 'reading-tests'],
    queryFn: ieltsReadingApi.adminListTests,
  });
  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ['admin', 'ielts', 'reading-tests'] });
  const remove = useMutation({
    mutationFn: (id: string) => ieltsReadingApi.adminDeleteTest(id),
    onSuccess: invalidate,
  });

  const tests = list.data ?? [];
  const askRemove = async (test: ReadingTest) => {
    if (await ask({ message: t('ieltsAdmin.reading.deleteConfirm', { title: test.title }), danger: true })) {
      remove.mutate(test.id);
    }
  };
  const modals = (
    <>
      {creatingOpen && <TestModal onClose={() => setCreatingOpen(false)} onSaved={invalidate} />}
      {editing && <TestModal test={editing} onClose={() => setEditing(null)} onSaved={invalidate} />}
    </>
  );

  // ── Телефон (docs/CURATOR-MOBILE-DESIGN.md §5.14) ─────────────────────────
  // Карточки → строки «название · вопросов N · N мин · активно»; два значка → «⋯» с листом.
  if (isMobile) {
    return (
      <div className="space-y-3">
        <MTitle
          title={t('ieltsAdmin.reading.title')}
          subtitle={t('ieltsAdmin.reading.subtitle')}
          right={<MAdd label={t('ieltsAdmin.reading.createBtn')} onClick={() => setCreatingOpen(true)} />}
        />
        {remove.error && <p className="px-0.5 text-[13px] text-error">{humanizeError(remove.error)}</p>}
        {list.isLoading ? (
          <p className="px-0.5 text-[13px] text-on-surface-variant">{t('common.loading')}</p>
        ) : list.error ? (
          <p className="px-0.5 text-[13px] text-error">{humanizeError(list.error)}</p>
        ) : !tests.length ? (
          <MEmpty icon="menu_book" title={t('ieltsAdmin.reading.noTests')} />
        ) : (
          <MBlock>
            {tests.map((test, i) => (
              <MRow
                key={test.id}
                wrap
                last={i === tests.length - 1}
                onClick={() => setEditing(test)}
                title={test.title}
                meta={[
                  t('ieltsAdmin.reading.questionCount', { count: test.questions?.length ?? 0 }),
                  typeof test.duration_min === 'number' ? t('ieltsAdmin.reading.durationMin', { count: test.duration_min }) : '',
                  test.is_active ? t('ieltsAdmin.reading.statusActive') : t('ieltsAdmin.reading.statusHidden'),
                ]
                  .filter(Boolean)
                  .join(' · ')}
                aside={<MoreButton onClick={() => setSheetFor(test)} />}
              />
            ))}
          </MBlock>
        )}
        <ActionSheet
          open={!!sheetFor}
          onClose={() => setSheetFor(null)}
          title={sheetFor?.title}
          actions={
            sheetFor
              ? [
                  { label: t('common.edit'), icon: 'edit', onSelect: () => setEditing(sheetFor) },
                  { label: t('common.delete'), icon: 'delete', danger: true, onSelect: () => void askRemove(sheetFor) },
                ]
              : []
          }
        />
        {modals}
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title={t('ieltsAdmin.reading.title')}
        subtitle={t('ieltsAdmin.reading.subtitle')}
        actions={
          <Button onClick={() => setCreatingOpen(true)}>
            <span className="material-symbols-outlined">add</span>
            {t('ieltsAdmin.reading.createBtn')}
          </Button>
        }
      />

      {remove.error && (
        <Card variant="subtle" className="mb-4 text-sm text-error">
          {humanizeError(remove.error)}
        </Card>
      )}

      {list.isLoading ? (
        <Card variant="subtle">{t('common.loading')}</Card>
      ) : list.error ? (
        <Card variant="subtle" className="text-error">{humanizeError(list.error)}</Card>
      ) : !tests.length ? (
        <Card variant="subtle" className="text-center text-on-surface-variant">
          {t('ieltsAdmin.reading.noTests')}{' '}
          <button
            type="button"
            onClick={() => setCreatingOpen(true)}
            className="font-semibold text-primary"
          >
            {t('ieltsAdmin.reading.createFirst')}
          </button>
        </Card>
      ) : (
        <ul className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {tests.map((test) => (
            <li key={test.id}>
              <Card className="flex h-full flex-col">
                <div className="flex flex-wrap items-center gap-2">
                  <Pill variant="primary">
                    {t('ieltsAdmin.reading.questionCount', {
                      count: test.questions?.length ?? 0,
                    })}
                  </Pill>
                  <Pill variant={test.is_active ? 'success' : 'neutral'}>
                    {test.is_active
                      ? t('ieltsAdmin.reading.statusActive')
                      : t('ieltsAdmin.reading.statusHidden')}
                  </Pill>
                  {typeof test.duration_min === 'number' && (
                    <Pill variant="neutral">
                      {t('ieltsAdmin.reading.durationMin', { count: test.duration_min })}
                    </Pill>
                  )}
                </div>
                <CardTitle className="mt-3">{test.title}</CardTitle>
                <div className="mt-auto flex gap-2 pt-3">
                  <Button variant="ghost" size="sm" onClick={() => setEditing(test)}>
                    <span className="material-symbols-outlined">edit</span>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => void askRemove(test)}
                  >
                    <span className="material-symbols-outlined">delete</span>
                  </Button>
                </div>
              </Card>
            </li>
          ))}
        </ul>
      )}

      {modals}
    </>
  );
}

function TestModal({
  test,
  onClose,
  onSaved,
}: {
  test?: ReadingTest;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const isEdit = Boolean(test);

  const [title, setTitle] = useState(test?.title ?? '');
  const [passageHtml, setPassageHtml] = useState(test?.passage_html ?? '');
  const [durationText, setDurationText] = useState(
    test?.duration_min != null ? String(test.duration_min) : '20',
  );
  const [isActive, setIsActive] = useState(test?.is_active ?? true);
  const [questions, setQuestions] = useState<ReadingQuestion[]>(
    test?.questions?.length ? test.questions.map(toEditableQuestion) : [newQuestion()],
  );

  const save = useMutation({
    mutationFn: () => {
      const dur = durationText.trim() === '' ? undefined : Number(durationText);
      const payload: ReadingTestInput = {
        title: title.trim(),
        passage_html: passageHtml,
        duration_min: dur != null && Number.isFinite(dur) ? dur : undefined,
        is_active: isActive,
        questions: questions.map(normalizeQuestion),
      };
      return isEdit
        ? ieltsReadingApi.adminUpdateTest(test!.id, payload)
        : ieltsReadingApi.adminCreateTest(payload);
    },
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });

  const updateQuestion = (idx: number, patch: Partial<ReadingQuestion>) =>
    setQuestions((qs) => qs.map((q, i) => (i === idx ? { ...q, ...patch } : q)));

  const removeQuestion = (idx: number) =>
    setQuestions((qs) => qs.filter((_, i) => i !== idx));

  const moveQuestion = (idx: number, dir: -1 | 1) =>
    setQuestions((qs) => {
      const next = [...qs];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return qs;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });

  const addQuestion = () => setQuestions((qs) => [...qs, newQuestion()]);

  const canSave =
    title.trim().length > 0 &&
    passageHtml.trim().length > 0 &&
    questions.length > 0 &&
    questions.every(isQuestionValid);
  const isMobile = useIsMobile();
  // На телефоне — «Сохранить» прибита внизу окна во весь экран (§5.14): вопросов бывает полтора десятка, и кнопка
  // в конце формы уезжала на много экранов вниз.
  const buttons = (
    <div className={isMobile ? 'grid grid-cols-2 gap-2' : 'flex justify-end gap-2 pt-2'}>
      <Button variant="ghost" onClick={onClose}>
        {t('common.cancel')}
      </Button>
      <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!canSave}>
        {isEdit ? t('common.save') : t('ieltsAdmin.reading.createBtn')}
      </Button>
    </div>
  );

  return (
    <Modal
      open
      onClose={onClose}
      title={isEdit ? t('ieltsAdmin.reading.editTest') : t('ieltsAdmin.reading.newTest')}
      phoneFull
      footer={isMobile ? buttons : undefined}
    >
      <div className="space-y-3">
        <Input
          label={t('ieltsAdmin.reading.fieldTitle')}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />

        <label className="block">
          <span className="mb-1.5 block text-xs font-semibold uppercase tracking-label text-on-surface-variant">
            {t('ieltsAdmin.reading.fieldPassage')}
          </span>
          <textarea
            className="min-h-[200px] w-full rounded-button bg-surface-container-lowest p-4 font-mono text-sm text-on-surface shadow-soft transition focus:shadow-ambient focus:outline-none"
            placeholder={t('ieltsAdmin.reading.fieldPassageHint')}
            value={passageHtml}
            onChange={(e) => setPassageHtml(e.target.value)}
          />
          <span className="mt-1 block text-xs text-on-surface-variant">
            {t('ieltsAdmin.reading.passageSanitizeHint')}
          </span>
        </label>

        <div className="grid gap-3 sm:grid-cols-2">
          <Input
            type="number"
            inputMode="numeric"
            min="1"
            max="120"
            label={t('ieltsAdmin.reading.fieldDuration')}
            hint={t('ieltsAdmin.reading.fieldDurationHint')}
            value={durationText}
            onChange={(e) => setDurationText(e.target.value)}
          />
          <Select
            label={t('ieltsAdmin.reading.fieldStatus')}
            value={isActive ? 'on' : 'off'}
            onChange={(e) => setIsActive(e.target.value === 'on')}
            options={[
              { value: 'on', label: t('ieltsAdmin.reading.statusActive') },
              { value: 'off', label: t('ieltsAdmin.reading.statusHidden') },
            ]}
          />
        </div>

        {/* ---- Questions editor (multi-question #5 pattern) ---- */}
        <div>
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-label text-on-surface-variant">
              {t('ieltsAdmin.reading.questionsTitle')}
            </span>
            <Button variant="ghost" size="sm" onClick={addQuestion}>
              <span className="material-symbols-outlined text-base">add</span>
              {t('ieltsAdmin.reading.addQuestion')}
            </Button>
          </div>

          <ol className="space-y-3">
            {questions.map((q, i) => (
              <li key={q.id}>
                <QuestionEditor
                  index={i + 1}
                  question={q}
                  isFirst={i === 0}
                  isLast={i === questions.length - 1}
                  canRemove={questions.length > 1}
                  onChange={(patch) => updateQuestion(i, patch)}
                  onRemove={() => removeQuestion(i)}
                  onMoveUp={() => moveQuestion(i, -1)}
                  onMoveDown={() => moveQuestion(i, 1)}
                />
              </li>
            ))}
          </ol>
          {/* На телефоне «Добавить вопрос» ещё и под списком (§5.14): сверху её не видно после десятого вопроса. */}
          {isMobile && (
            <Button variant="secondary" className="mt-3 w-full" onClick={addQuestion} data-add-question-bottom>
              <span className="material-symbols-outlined text-base">add</span>
              {t('ieltsAdmin.reading.addQuestion')}
            </Button>
          )}
        </div>

        {save.error && <p className="text-sm text-error">{humanizeError(save.error)}</p>}
        {!canSave && (
          <p className="text-xs text-on-surface-variant">
            {t('ieltsAdmin.reading.validationHint')}
          </p>
        )}

        {!isMobile && buttons}
      </div>
    </Modal>
  );
}

function QuestionEditor({
  index,
  question,
  isFirst,
  isLast,
  canRemove,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
}: {
  index: number;
  question: ReadingQuestion;
  isFirst: boolean;
  isLast: boolean;
  canRemove: boolean;
  onChange: (patch: Partial<ReadingQuestion>) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [sheet, setSheet] = useState(false);
  const isIndex = INDEX_TYPES.includes(question.type);

  // When the type changes, reset answer/options to a valid default for it.
  const changeType = (type: ReadingQuestionType) => {
    if (INDEX_TYPES.includes(type)) {
      onChange({ type, options: question.options?.length ? question.options : ['', ''], answer: 0 });
    } else if (type === 'true_false_ng') {
      onChange({ type, options: undefined, answer: 'true' });
    } else if (type === 'yes_no_ng') {
      onChange({ type, options: undefined, answer: 'yes' });
    } else {
      onChange({ type, options: undefined, answer: '' });
    }
  };

  const setOption = (oi: number, value: string) => {
    const opts = [...(question.options ?? [])];
    opts[oi] = value;
    onChange({ options: opts });
  };
  const addOption = () => onChange({ options: [...(question.options ?? []), ''] });
  const removeOption = (oi: number) => {
    const opts = (question.options ?? []).filter((_, i) => i !== oi);
    const ans = typeof question.answer === 'number' && question.answer >= opts.length
      ? Math.max(0, opts.length - 1)
      : question.answer;
    onChange({ options: opts, answer: ans });
  };

  return (
    <div className="rounded-card bg-surface-container-low p-3 ring-1 ring-inset ring-outline-variant/20">
      <div className="mb-2 flex items-center gap-2">
        <span className="text-sm font-bold text-primary">{index}</span>
        <Select
          className="flex-1"
          value={question.type}
          onChange={(e) => changeType(e.target.value as ReadingQuestionType)}
          options={QUESTION_TYPES.map((qt) => ({
            value: qt,
            label: t(`ieltsAdmin.reading.qtype.${qt}`),
          }))}
        />
        {isMobile ? (
          // Телефон (§5.14): три значка 32 px сжимали выбор типа — «Выше / Ниже / Удалить» листом «⋯», только применимые.
          <>
            <MoreButton onClick={() => setSheet(true)} />
            <ActionSheet
              open={sheet}
              onClose={() => setSheet(false)}
              title={t('ieltsAdmin.reading.questionsTitle')}
              actions={[
                ...(!isFirst ? [{ label: t('adminMarathonDetail.moveUp'), icon: 'arrow_upward', onSelect: onMoveUp }] : []),
                ...(!isLast ? [{ label: t('adminMarathonDetail.moveDown'), icon: 'arrow_downward', onSelect: onMoveDown }] : []),
                ...(canRemove ? [{ label: t('common.delete'), icon: 'delete', danger: true, onSelect: onRemove }] : []),
              ]}
            />
          </>
        ) : (
          <>
            <Button variant="ghost" size="sm" onClick={onMoveUp} disabled={isFirst}>
              <span className="material-symbols-outlined text-base">arrow_upward</span>
            </Button>
            <Button variant="ghost" size="sm" onClick={onMoveDown} disabled={isLast}>
              <span className="material-symbols-outlined text-base">arrow_downward</span>
            </Button>
            <Button variant="ghost" size="sm" onClick={onRemove} disabled={!canRemove}>
              <span className="material-symbols-outlined text-base">delete</span>
            </Button>
          </>
        )}
      </div>

      <Input
        label={t('ieltsAdmin.reading.fieldPrompt')}
        value={question.prompt}
        onChange={(e) => onChange({ prompt: e.target.value })}
      />

      {/* Option list + index answer for MC / matching_headings */}
      {isIndex && (
        <div className="mt-2">
          <span className="mb-1.5 block text-xs font-semibold uppercase tracking-label text-on-surface-variant">
            {t('ieltsAdmin.reading.fieldOptions')}
          </span>
          <div className="space-y-1.5">
            {(question.options ?? []).map((opt, oi) => (
              <div key={oi} className="flex items-center gap-2">
                <input
                  type="radio"
                  name={`ans-${question.id}`}
                  checked={question.answer === oi}
                  onChange={() => onChange({ answer: oi })}
                  title={t('ieltsAdmin.reading.markCorrect')}
                  className="accent-primary"
                />
                <input
                  type="text"
                  value={opt}
                  onChange={(e) => setOption(oi, e.target.value)}
                  placeholder={t('ieltsAdmin.reading.optionPlaceholder', { n: oi + 1 })}
                  className="flex-1 rounded-button bg-surface-container-lowest px-3 py-1.5 text-sm text-on-surface outline-none ring-1 ring-inset ring-outline-variant/30 focus:ring-2 focus:ring-primary"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeOption(oi)}
                  disabled={(question.options?.length ?? 0) <= 2}
                >
                  <span className="material-symbols-outlined text-base">close</span>
                </Button>
              </div>
            ))}
          </div>
          <Button variant="ghost" size="sm" className="mt-1.5" onClick={addOption}>
            <span className="material-symbols-outlined text-base">add</span>
            {t('ieltsAdmin.reading.addOption')}
          </Button>
          <p className="mt-1 text-xs text-on-surface-variant">
            {t('ieltsAdmin.reading.markCorrectHint')}
          </p>
        </div>
      )}

      {/* Enum answer for TF/NG */}
      {question.type === 'true_false_ng' && (
        <Select
          className="mt-2"
          label={t('ieltsAdmin.reading.fieldCorrectAnswer')}
          value={String(question.answer ?? 'true')}
          onChange={(e) => onChange({ answer: e.target.value })}
          options={TF_NG_OPTIONS.map((o) => ({
            value: o,
            label: t(`ielts.reading.tfng.${o}`),
          }))}
        />
      )}

      {/* Enum answer for YN/NG */}
      {question.type === 'yes_no_ng' && (
        <Select
          className="mt-2"
          label={t('ieltsAdmin.reading.fieldCorrectAnswer')}
          value={String(question.answer ?? 'yes')}
          onChange={(e) => onChange({ answer: e.target.value })}
          options={YN_NG_OPTIONS.map((o) => ({
            value: o,
            label: t(`ielts.reading.ynng.${o}`),
          }))}
        />
      )}

      {/* Free-text answer for sentence_completion / short_answer */}
      {(question.type === 'sentence_completion' || question.type === 'short_answer') && (
        <Input
          className="mt-2"
          label={t('ieltsAdmin.reading.fieldCorrectAnswer')}
          hint={t('ieltsAdmin.reading.textAnswerHint')}
          value={typeof question.answer === 'string' ? question.answer : ''}
          onChange={(e) => onChange({ answer: e.target.value })}
        />
      )}
    </div>
  );
}

/** A question is valid when it has a prompt and a usable answer key. */
function isQuestionValid(q: ReadingQuestion): boolean {
  if (!q.prompt.trim()) return false;
  if (INDEX_TYPES.includes(q.type)) {
    const opts = q.options ?? [];
    return (
      opts.length >= 2 &&
      opts.every((o) => o.trim().length > 0) &&
      typeof q.answer === 'number' &&
      q.answer >= 0 &&
      q.answer < opts.length
    );
  }
  if (q.type === 'true_false_ng') return TF_NG_OPTIONS.includes(q.answer as never);
  if (q.type === 'yes_no_ng') return YN_NG_OPTIONS.includes(q.answer as never);
  // text answer
  return typeof q.answer === 'string' && q.answer.trim().length > 0;
}

/** Coerce a question loaded from the server into the editor's shape. Index types
 * arrive with answer as a stringified index ("2"); the editor uses a numeric
 * answer for the radio selection + validity check, so parse it back. */
function toEditableQuestion(q: ReadingQuestion): ReadingQuestion {
  if (INDEX_TYPES.includes(q.type)) {
    const idx = Number(q.answer);
    return { ...q, answer: Number.isFinite(idx) ? idx : 0 };
  }
  return { ...q };
}

/** Trim text values before sending so the server stores a clean key. The backend
 * binds questions[].answer as a string; index types hold the option index, so it
 * must be stringified (a numeric answer would fail JSON binding → 400). */
function normalizeQuestion(q: ReadingQuestion): ReadingQuestion {
  const base: ReadingQuestion = { ...q, prompt: q.prompt.trim() };
  if (INDEX_TYPES.includes(q.type)) {
    return {
      ...base,
      options: (q.options ?? []).map((o) => o.trim()),
      answer: String(q.answer ?? 0),
    };
  }
  if (typeof base.answer === 'string') {
    base.answer = base.answer.trim();
  }
  return base;
}
